import { useQuery } from '@apollo/client';
import { Hub, HubCallback } from '@aws-amplify/core';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Amplify } from 'aws-amplify';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import routes from 'routes';
import { UserPool, UserPoolVariables } from 'types/apollo/UserPool';
import { AMPLIFY_KEYS, isAddingAccount } from 'utils/accounts';
import { cancelAddAccount } from 'utils/accounts-add';
import amplifyConfigs from '../../../configs/amplify-config';
import AktifyLogoImage from '../../../public/aktify-logo.svg';
import utahBackground from '../../../public/utah-bg.jpeg';
import { LoginOrCreateAccount } from '../login-or-create-account';
import { FormData, LoginForm } from './login-form';
import { UserQuery } from './login.graphql';

const Background = styled(Box)(() => ({
  minHeight: '100vh',
  background: `url(${utahBackground})`,
  backgroundSize: 'cover',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
}));

const StyledLoginBox = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.85)',
  padding: theme.spacing(3),
  boxShadow: '0px 0px 5px 0px rgba(0,0,0,0.8)',
  borderRadius: theme.spacing(1),
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  marginLeft: 'calc(5px + 1vh)',
  marginRight: 'calc(5px + 1vh)',
  marginTop: 'clamp(25px, 10vh, 300px)',
  marginBottom: 'calc(10px + 10vh)',
  minWidth: '420px',
}));

const AktifyLogo = styled(Box)`
  margin: auto auto 20px auto;
  height: 32px;
  content: url(${AktifyLogoImage});
`;

const getEmailFromUrl = () => {
  const match = window.location.search.match(/[?&]email=([^&]*)/);
  if (match) {
    return decodeURIComponent(match[1]);
  }
  return undefined;
};

export const Login: FC<{ error?: string; originalUrl: string }> = ({ error, originalUrl }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [identityProviders, setIdentityProviders] = useState<string[] | null>(null);
  const [signUpEnabled, setSignUpEnabled] = useState<boolean | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [domainLoading, setDomainLoading] = useState(false);
  const [lookupEmail, setLookupEmail] = useState<string | undefined>(undefined);
  const { refetch } = useQuery<UserPool, UserPoolVariables>(UserQuery, {
    skip: true,
  });

  const listener: HubCallback = (data) => {
    console.debug(data.payload.event, data.payload);
    switch (data.payload.event) {
      case 'confirmSignUp':
        setConfirmed(true);
        break;
      case 'autoSignIn':
      case 'signedIn':
        if (originalUrl) {
          navigate(originalUrl);
        } else {
          navigate(routes.home.path);
        }
        break;
    }
  };

  useEffect(() => {
    return Hub.listen('auth', listener.bind(this));
  }, []);

  const form = useForm<FormData>();
  const [formError, setFormError] = useState<string>(error ?? '');

  const lookupUserPool = async (email: string, storeEmail?: string) => {
    const result = await refetch({ email: email.toLowerCase() });
    if (result.data.userPool) {
      const {
        region,
        userPoolId,
        clientId,
        cognitoHostedDomain,
        identityProviders,
        signUpEnabled,
      } = result.data.userPool;

      if (clientId && cognitoHostedDomain) {
        localStorage.setItem(AMPLIFY_KEYS.region, region);
        localStorage.setItem(AMPLIFY_KEYS.userPoolId, userPoolId);
        localStorage.setItem(AMPLIFY_KEYS.clientId, clientId);
        localStorage.setItem(AMPLIFY_KEYS.cognitoHostedDomain, cognitoHostedDomain);
        const authConfig: any = {
          ...amplifyConfigs().Auth,
          userPoolId,
          region,
          oauth: {
            ...amplifyConfigs().Auth?.Cognito.loginWith?.oauth,
            domain: cognitoHostedDomain,
          },
        };

        authConfig.clientId = clientId;

        Amplify.configure({
          Auth: authConfig,
        });

        setFormError('');
        setIdentityProviders(identityProviders);
        setSignUpEnabled(signUpEnabled);
        if (storeEmail) {
          setLookupEmail(storeEmail);
        }
        return true;
      } else {
        setFormError(
          'There is an account for this email, but it is not quite ready for login. Contact your administrator or Aktify Account Manager to finish set up.',
        );
      }
    } else {
      setFormError(
        'There is no account for this email. Contact your Aktify Account Manager to set up an account.',
      );
    }
    return false;
  };

  // Auto-lookup if domain is provided in URL
  useEffect(() => {
    const domain = searchParams.get('domain');
    if (domain) {
      setDomainLoading(true);
      const emailFromUrl = getEmailFromUrl();
      if (emailFromUrl) {
        setLookupEmail(emailFromUrl);
      }
      lookupUserPool(`user@${domain}`).finally(() => setDomainLoading(false));
    }
  }, []);

  const onSubmit = form.handleSubmit(async (data) => {
    await lookupUserPool(data.email, data.email);
  });

  // Mid add-account flow, "go home" must clear the add flag and restore the previous account,
  // or the AuthWrapper gate would bounce straight back to /login.
  const goHome = () => {
    if (isAddingAccount()) {
      cancelAddAccount();
    } else {
      window.location.href = '/';
    }
  };

  return (
    <Background>
      <StyledLoginBox>
        {identityProviders && signUpEnabled != null && !confirmed ? (
          <LoginOrCreateAccount
            originalUrl={originalUrl}
            identityProviders={identityProviders}
            signUpEnabled={signUpEnabled}
            defaultEmail={lookupEmail}
            backToEmailPrompt={() => {
              setIdentityProviders(null);
              setSignUpEnabled(null);
              setLookupEmail(undefined);
            }}
            onGoHome={goHome}
            signedInHint={
              isAddingAccount() ? 'You can only have one account per user pool.' : undefined
            }
          />
        ) : (
          <>
            <AktifyLogo />

            {confirmed || domainLoading ? (
              <>
                <CircularProgress />
                <Typography>{domainLoading ? 'Loading...' : 'Signing in...'}</Typography>
              </>
            ) : (
              <FormProvider {...form}>
                <LoginForm onSubmit={onSubmit} formError={formError} />
              </FormProvider>
            )}
          </>
        )}
        {isAddingAccount() && (
          <Button variant="text" onClick={() => cancelAddAccount()}>
            Cancel
          </Button>
        )}
      </StyledLoginBox>
    </Background>
  );
};
